import { useEffect, useRef, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useWalletStore } from '@/stores/useWalletStore'
import { useMiningStore } from '@/stores/useMiningStore'
import { useEosTransaction } from '@/hooks/useEosTransaction'
import { getCoinIcon } from '@/lib/common'
import { CommonFooter } from '@/components/layout'
import { toFixed } from '@/lib/decimal'
import noDataImg from '@/assets/images/noData.svg'

export function LendMiningPage() {
  const { t } = useTranslation()
  const { isLogin, session } = useWalletStore()
  const { lendFarms, fetchLendFarms } = useMiningStore()
  const { transact, loading: txLoading } = useEosTransaction()
  const [menuActive, setMenuActive] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const accountName = session?.account?.name

  useEffect(() => {
    fetchLendFarms(accountName)
    timerRef.current = setInterval(() => fetchLendFarms(accountName), 10000)
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [accountName])

  const filteredFarms = useMemo(() => {
    return lendFarms.filter((f) => f.status === menuActive)
  }, [lendFarms, menuActive])

  // Merge deposit/borrow farms by contract
  const mergedFarms = useMemo(() => {
    const map = new Map<string, { deposit?: typeof filteredFarms[0]; borrow?: typeof filteredFarms[0] }>()
    for (const farm of filteredFarms) {
      const key = farm.contract || farm.token.sym.split(',')[1]
      const entry = map.get(key) || {}
      if (farm.token.contract === 'dtoken.ttr') {
        entry.deposit = farm
      } else {
        entry.borrow = farm
      }
      map.set(key, entry)
    }
    return Array.from(map.entries())
  }, [filteredFarms])

  // Stats
  const totalTvl = useMemo(() => {
    return lendFarms.reduce((sum, f) => sum + (f.total_balance_usdt || 0), 0)
  }, [lendFarms])

  const totalReward = useMemo(() => {
    return lendFarms.reduce((sum, f) => sum + (f.reward || 0), 0)
  }, [lendFarms])

  const totalEarned = useMemo(() => {
    return lendFarms.reduce((sum, f) => sum + (f.claimed || 0) + (f.locked || 0) + (f.released || 0), 0)
  }, [lendFarms])

  const handleClaimAll = async () => {
    if (!session) return
    const account = session.account
    const actions = [{
      account: 'lendmine.ttr',
      name: 'claimall',
      authorization: [{ actor: account.name, permission: account.authority || 'active' }],
      data: { owner: account.name },
    }]
    const result = await transact(actions, t('swap.success'))
    if (result) fetchLendFarms(accountName)
  }

  return (
    <div className="w-full flex justify-center px-4 md:px-[32px] md:mt-[32px]">
      <div className="w-full max-w-[600px]">
        {/* Stats board */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-[8px] mb-[16px]">
          {[
            { label: t('mining.totalReward'), value: totalReward > 0 ? toFixed(totalReward / 1e6, 4) + ' TTR' : '--' },
            { label: t('mining.tvl'), value: totalTvl > 0 ? '$' + toFixed(totalTvl, 2) : '--' },
            { label: t('mining.dailyEmission'), value: '86,400 TTR' },
            { label: t('mining.earned'), value: totalEarned > 0 ? toFixed(totalEarned / 1e6, 4) + ' TTR' : '--' },
          ].map((item) => (
            <div key={item.label} className="bg-white rounded-[16px] shadow-[0_0_8px_1px_rgba(61,98,50,0.14)] p-[12px]">
              <p className="text-[12px] text-[#999] mb-[4px]">{item.label}</p>
              <p className="text-[14px] font-bold text-foreground">{item.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center justify-between mb-[16px]">
          <div className="flex gap-[8px]">
            <button
              onClick={() => setMenuActive(0)}
              className={`px-[16px] py-[6px] rounded-full text-[13px] font-semibold cursor-pointer ${menuActive === 0 ? 'bg-[#498846] text-white' : 'bg-white text-[#666] border border-[#e5e5e5]'}`}
            >
              {t('mining.live')}
            </button>
            <button
              onClick={() => setMenuActive(1)}
              className={`px-[16px] py-[6px] rounded-full text-[13px] font-semibold cursor-pointer ${menuActive === 1 ? 'bg-[#498846] text-white' : 'bg-white text-[#666] border border-[#e5e5e5]'}`}
            >
              {t('mining.finished')}
            </button>
          </div>
          {isLogin && totalReward > 0 && (
            <button
              onClick={handleClaimAll}
              disabled={txLoading}
              className="px-[16px] py-[6px] rounded-full text-[13px] font-semibold bg-[#498846] text-white cursor-pointer disabled:opacity-60"
            >
              {t('mining.claim')} All
            </button>
          )}
        </div>

        {/* Farm list */}
        {mergedFarms.length === 0 ? (
          <div className="bg-white rounded-[26px] shadow-[0_0_8px_1px_rgba(61,98,50,0.14)] px-[16px] py-[24px]">
            <div className="flex flex-col items-center py-[30px]">
              <img src={noDataImg} className="w-[120px] mb-[12px]" alt="" />
              <span className="text-[14px] text-[#999]">{t('public.noData')}</span>
            </div>
          </div>
        ) : (
          <div className="space-y-[12px]">
            {mergedFarms.map(([key, { deposit, borrow }]) => {
              const symbol = deposit?.symbol || borrow?.symbol || key
              const contract = deposit?.contract || borrow?.contract || ''
              return (
                <div key={key} className="bg-white rounded-[26px] shadow-[0_0_8px_1px_rgba(61,98,50,0.14)] px-[16px] py-[16px]">
                  {/* Header */}
                  <div className="flex items-center gap-[8px] mb-[12px]">
                    <img src={getCoinIcon(contract, symbol)} className="w-[28px] h-[28px] rounded-full bg-gray-100" alt="" />
                    <span className="text-[14px] font-semibold">{symbol}</span>
                  </div>

                  {/* Deposit farm */}
                  {deposit && (
                    <div className="mb-[10px] p-[10px] rounded-[12px] bg-[#f9fdf9]">
                      <div className="text-[12px] text-[#498846] font-semibold mb-[6px]">{t('lend.deposit')}</div>
                      <div className="space-y-[4px] text-[13px]">
                        <div className="flex justify-between">
                          <span className="text-[#666]">APR</span>
                          <span className="text-[#498846] font-semibold">{deposit.apr > 0 ? toFixed(deposit.apr * 100, 2) + '%' : '--'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#666]">{t('mining.tvl')}</span>
                          <span>${deposit.total_balance_usdt > 0 ? toFixed(deposit.total_balance_usdt, 2) : '--'}</span>
                        </div>
                        {deposit.weight > 0 && (
                          <div className="flex justify-between">
                            <span className="text-[#666]">Weight</span>
                            <span>{deposit.weight}x</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Borrow farm */}
                  {borrow && (
                    <div className="p-[10px] rounded-[12px] bg-[#fdf9f9]">
                      <div className="text-[12px] text-[#c0392b] font-semibold mb-[6px]">{t('lend.borrow')}</div>
                      <div className="space-y-[4px] text-[13px]">
                        <div className="flex justify-between">
                          <span className="text-[#666]">APR</span>
                          <span className="text-[#c0392b] font-semibold">{borrow.apr > 0 ? toFixed(borrow.apr * 100, 2) + '%' : '--'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#666]">{t('mining.tvl')}</span>
                          <span>${borrow.total_balance_usdt > 0 ? toFixed(borrow.total_balance_usdt, 2) : '--'}</span>
                        </div>
                        {borrow.weight > 0 && (
                          <div className="flex justify-between">
                            <span className="text-[#666]">Weight</span>
                            <span>{borrow.weight}x</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
        <CommonFooter />
      </div>
    </div>
  )
}
